import React, { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import Header from "../components/Header";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSystemYears,
  addYearToAllUsers,
  removeYearFromAllUsers,
  clearMessages,
} from "../redux/reducers/yearManagementSlice";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { MdCalendarToday } from "react-icons/md";

const YearManagement = () => {
  const dispatch = useDispatch();
  const { years, loading, actionLoading, error, successMessage } = useSelector(
    (state) => state.yearManagement
  );

  const [addYear, setAddYear] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null);

  useEffect(() => {
    dispatch(fetchSystemYears());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => dispatch(clearMessages()), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error, dispatch]);

  const handleAdd = () => {
    const year = Number(addYear);
    if (!year || year < 2000 || year > 2100) return;
    dispatch(addYearToAllUsers(year));
    setAddYear("");
  };

  const handleRemoveConfirm = (year) => {
    setConfirmRemove(year);
  };

  const handleRemove = () => {
    if (!confirmRemove) return;
    dispatch(removeYearFromAllUsers(confirmRemove));
    setConfirmRemove(null);
  };

  return (
    <AdminLayout>
      <div className="p-4">
        <Header
          title="Year Management"
          subTitle="Admin"
          description="Add or remove fiscal years across all active users"
        />

        {successMessage && (
          <div className="mb-4 p-3 rounded-xl bg-green-100 text-green-700 text-sm">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-100 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Add Year */}
        <fieldset className="border-2 rounded-xl px-6 py-5 mb-6">
          <legend className="font-bold px-1">Add Year to All Users</legend>
          <p className="text-sm text-gray-500 mb-4">
            Adds the specified year (Projected, Actual, Student Drops & Clinic Sales) to every active user.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              placeholder={`e.g. ${new Date().getFullYear() + 1}`}
              value={addYear}
              onChange={(e) => setAddYear(e.target.value)}
              className="border-2 rounded-xl px-4 py-2 w-40 text-sm focus:outline-none focus:border-purple-400"
              min="2000"
              max="2100"
            />
            <button
              onClick={handleAdd}
              disabled={actionLoading || !addYear}
              className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-5 py-2 rounded-xl text-sm font-semibold"
            >
              <FiPlus />
              {actionLoading ? "Adding..." : "Add Year"}
            </button>
          </div>
        </fieldset>

        {/* Current Years */}
        <fieldset className="border-2 rounded-xl px-6 py-5">
          <legend className="font-bold px-1">Current Years in System</legend>
          <p className="text-sm text-gray-500 mb-4">
            These years exist across program data. Removing a year deletes all its data permanently.
          </p>

          {loading ? (
            <p className="text-sm text-gray-400">Loading years...</p>
          ) : years.length === 0 ? (
            <p className="text-sm text-gray-400">No years found in the system.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {years.map((year) => (
                <div
                  key={year}
                  className="flex items-center gap-2 border-2 rounded-xl px-4 py-2 bg-gray-50"
                >
                  <MdCalendarToday className="text-purple-500" />
                  <span className="font-semibold text-sm">{year}</span>
                  <button
                    onClick={() => handleRemoveConfirm(year)}
                    className="text-red-400 hover:text-red-600 ml-1"
                    title={`Remove ${year}`}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>
          )}
        </fieldset>

        {/* Confirm Remove Dialog */}
        {confirmRemove && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl">
              <h3 className="text-lg font-bold mb-2 text-red-600">Remove Year {confirmRemove}?</h3>
              <p className="text-sm text-gray-600 mb-6">
                This will permanently delete all <strong>{confirmRemove}</strong> data (programs, clinic
                sales) for every user. This cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmRemove(null)}
                  className="px-5 py-2 rounded-xl border-2 text-sm font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemove}
                  disabled={actionLoading}
                  className="px-5 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {actionLoading ? "Removing..." : "Yes, Remove"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default YearManagement;
